import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { useAuth } from '@calefy-inc/authentication';
import { RouteComponentProps } from '@reach/router';
import { useEffect, useState } from 'react';
import { styled } from '@mui/material';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { DisplayInlineError } from '../common/ErrorHandling';
import { graphql } from '../../gql';
import { formatDateTimeForDisplay } from '../../util';
import type {
  PalApiBindRequestRequestsQuery,
  PalApiBindRequestRequestsQueryVariables,
  PalApiBindRequestResponsesQuery,
  PalApiBindRequestResponsesQueryVariables,
  PalQuoteRequestRequestsQuery,
  PalQuoteRequestRequestsQueryVariables,
  PalQuoteRequestResponsesQuery,
  PalQuoteRequestResponsesQueryVariables,
} from '../../gql/graphql';

const PAGE_SIZE = 20;

interface PalSyzlApiComponentProps extends RouteComponentProps {}

export const PalSyzlApiComponent = (_props: PalSyzlApiComponentProps) => {
  return (
    <PageSection>
      <Typography variant='h5' component='h1' gutterBottom>
        Pal {'<>'} Syzl API
      </Typography>
      <PalQuoteRequestRequestsTable />
      <PalQuoteRequestResponsesTable />
      <PalApiBindRequestRequestsTable />
      <PalApiBindRequestResponsesTable />
    </PageSection>
  );
};

type PalQuoteRequestRequestNode = NonNullable<
  NonNullable<
    NonNullable<
      PalQuoteRequestRequestsQuery['palQuoteRequestRequests']
    >['edges'][number]
  >['node']
>;
type PalQuoteRequestResponseNode = NonNullable<
  NonNullable<
    NonNullable<
      PalQuoteRequestResponsesQuery['palQuoteRequestResponses']
    >['edges'][number]
  >['node']
>;
type PalApiBindRequestRequestNode = NonNullable<
  NonNullable<
    NonNullable<
      PalApiBindRequestRequestsQuery['palApiBindRequestRequests']
    >['edges'][number]
  >['node']
>;
type PalApiBindRequestResponseNode = NonNullable<
  NonNullable<
    NonNullable<
      PalApiBindRequestResponsesQuery['palApiBindRequestResponses']
    >['edges'][number]
  >['node']
>;

const PalQuoteRequestRequestsTable = () => {
  const { token } = useAuth();
  const [runQuery, { data, loading, error, fetchMore, networkStatus }] =
    useLazyQuery<
      PalQuoteRequestRequestsQuery,
      PalQuoteRequestRequestsQueryVariables
    >(PAL_QUOTE_REQUEST_REQUESTS, {
      notifyOnNetworkStatusChange: true,
      variables: { token, first: PAGE_SIZE },
    });
  const [jsonDialogValue, setJsonDialogValue] = useState<string | null>(null);
  useEffect(() => {
    if (token) {
      runQuery();
    }
  }, [token, runQuery]);

  const rows =
    data?.palQuoteRequestRequests?.edges
      .map((edge) => edge?.node)
      .filter((node): node is PalQuoteRequestRequestNode => Boolean(node)) ??
    [];
  const hasNextPage = data?.palQuoteRequestRequests?.pageInfo?.hasNextPage;
  const endCursor = data?.palQuoteRequestRequests?.pageInfo?.endCursor;
  const showBottomLoader =
    loading && (rows.length === 0 || networkStatus === NetworkStatus.fetchMore);

  if (!token) {
    return null;
  }

  return (
    <TableSection>
      <SectionTitle>PalQuoteRequestRequest</SectionTitle>
      <ButtonWrap>
        <Button type='button' variant='outlined' onClick={() => runQuery()}>
          {rows.length > 0 ? 'Refresh' : 'Load'}
        </Button>
      </ButtonWrap>
      <DisplayInlineError error={error} />
      <ScrollTableContainer>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <HeaderCell>ID</HeaderCell>
              <HeaderCell>Created At</HeaderCell>
              <HeaderCell>Client</HeaderCell>
              <HeaderCell>Facility</HeaderCell>
              <HeaderCell>Status Code</HeaderCell>
              <HeaderCell>Raw Response</HeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              return (
                <TableRow key={row.id}>
                  <BodyCell>{row.id}</BodyCell>
                  <BodyCell>
                    {formatDateTimeForDisplay(row.modelCreatedAt)}
                  </BodyCell>
                  <BodyCell>
                    {row.client
                      ? `${row.client.firstName} ${row.client.lastName}`
                      : '—'}
                  </BodyCell>
                  <BodyCell>{row.facility?.name ?? '—'}</BodyCell>
                  <BodyCell>
                    {row.responseStatusCode !== null &&
                    row.responseStatusCode !== undefined
                      ? String(row.responseStatusCode)
                      : '—'}
                  </BodyCell>
                  <BodyCell>
                    <JsonPreviewButton
                      value={row.responseText}
                      onOpen={setJsonDialogValue}
                    />
                  </BodyCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ScrollTableContainer>
      {hasNextPage ? (
        <LoadMoreWrap>
          <Button
            type='button'
            variant='outlined'
            disabled={loading || !endCursor}
            onClick={() => {
              if (endCursor) {
                fetchMore({
                  variables: { token, first: PAGE_SIZE, after: endCursor },
                  updateQuery: (
                    previous: PalQuoteRequestRequestsQuery,
                    { fetchMoreResult },
                  ) => {
                    if (!fetchMoreResult?.palQuoteRequestRequests) {
                      return previous;
                    }
                    return {
                      palQuoteRequestRequests: {
                        ...fetchMoreResult.palQuoteRequestRequests,
                        edges: [
                          ...(previous.palQuoteRequestRequests?.edges ?? []),
                          ...(fetchMoreResult.palQuoteRequestRequests.edges ??
                            []),
                        ],
                      },
                    };
                  },
                });
              }
            }}
          >
            Load More
          </Button>
        </LoadMoreWrap>
      ) : null}
      {showBottomLoader ? (
        <TableLoadingFooter>
          <CircularProgress size={24} />
        </TableLoadingFooter>
      ) : null}
      <JsonViewerDialog
        value={jsonDialogValue}
        onClose={() => setJsonDialogValue(null)}
      />
    </TableSection>
  );
};

const PalQuoteRequestResponsesTable = () => {
  const { token } = useAuth();
  const [runQuery, { data, loading, error, fetchMore, networkStatus }] =
    useLazyQuery<
      PalQuoteRequestResponsesQuery,
      PalQuoteRequestResponsesQueryVariables
    >(PAL_QUOTE_REQUEST_RESPONSES, {
      notifyOnNetworkStatusChange: true,
      variables: { token, first: PAGE_SIZE },
    });
  const [jsonDialogValue, setJsonDialogValue] = useState<string | null>(null);
  useEffect(() => {
    if (token) {
      runQuery();
    }
  }, [token, runQuery]);
  const rows =
    data?.palQuoteRequestResponses?.edges
      .map((edge) => edge?.node)
      .filter((node): node is PalQuoteRequestResponseNode => Boolean(node)) ??
    [];
  const hasNextPage = data?.palQuoteRequestResponses?.pageInfo?.hasNextPage;
  const endCursor = data?.palQuoteRequestResponses?.pageInfo?.endCursor;
  const showBottomLoader =
    loading && (rows.length === 0 || networkStatus === NetworkStatus.fetchMore);

  if (!token) {
    return null;
  }

  return (
    <TableSection>
      <SectionTitle>PalQuoteRequestResponse</SectionTitle>
      <ButtonWrap>
        <Button type='button' variant='outlined' onClick={() => runQuery()}>
          {rows.length > 0 ? 'Refresh' : 'Load'}
        </Button>
      </ButtonWrap>
      <DisplayInlineError error={error} />
      <ScrollTableContainer>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <HeaderCell>ID</HeaderCell>
              <HeaderCell>Created At</HeaderCell>
              <HeaderCell>Quote ID</HeaderCell>
              <HeaderCell>Policy Created</HeaderCell>
              <HeaderCell>Total</HeaderCell>
              <HeaderCell>Errors JSON</HeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              return (
                <TableRow key={row.id}>
                  <BodyCell>{row.id}</BodyCell>
                  <BodyCell>
                    {formatDateTimeForDisplay(row.modelCreatedAt)}
                  </BodyCell>
                  <BodyCell>
                    {row.quoteId !== null && row.quoteId !== undefined
                      ? String(row.quoteId)
                      : '—'}
                  </BodyCell>
                  <BodyCell>{row.policyCreated ? 'Yes' : 'No'}</BodyCell>
                  <BodyCell>{row.total}</BodyCell>
                  <BodyCell>
                    <JsonPreviewButton
                      value={row.errors}
                      onOpen={setJsonDialogValue}
                    />
                  </BodyCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ScrollTableContainer>
      {hasNextPage ? (
        <LoadMoreWrap>
          <Button
            type='button'
            variant='outlined'
            disabled={loading || !endCursor}
            onClick={() => {
              if (endCursor) {
                fetchMore({
                  variables: { token, first: PAGE_SIZE, after: endCursor },
                  updateQuery: (
                    previous: PalQuoteRequestResponsesQuery,
                    { fetchMoreResult },
                  ) => {
                    if (!fetchMoreResult?.palQuoteRequestResponses) {
                      return previous;
                    }
                    return {
                      palQuoteRequestResponses: {
                        ...fetchMoreResult.palQuoteRequestResponses,
                        edges: [
                          ...(previous.palQuoteRequestResponses?.edges ?? []),
                          ...(fetchMoreResult.palQuoteRequestResponses.edges ??
                            []),
                        ],
                      },
                    };
                  },
                });
              }
            }}
          >
            Load More
          </Button>
        </LoadMoreWrap>
      ) : null}
      {showBottomLoader ? (
        <TableLoadingFooter>
          <CircularProgress size={24} />
        </TableLoadingFooter>
      ) : null}
      <JsonViewerDialog
        value={jsonDialogValue}
        onClose={() => setJsonDialogValue(null)}
      />
    </TableSection>
  );
};

const PalApiBindRequestRequestsTable = () => {
  const { token } = useAuth();
  const [runQuery, { data, loading, error, fetchMore, networkStatus }] =
    useLazyQuery<
      PalApiBindRequestRequestsQuery,
      PalApiBindRequestRequestsQueryVariables
    >(PAL_API_BIND_REQUEST_REQUESTS, {
      notifyOnNetworkStatusChange: true,
      variables: { token, first: PAGE_SIZE },
    });
  const [jsonDialogValue, setJsonDialogValue] = useState<string | null>(null);
  useEffect(() => {
    if (token) {
      runQuery();
    }
  }, [token, runQuery]);
  const rows =
    data?.palApiBindRequestRequests?.edges
      .map((edge) => edge?.node)
      .filter((node): node is PalApiBindRequestRequestNode => Boolean(node)) ??
    [];
  const hasNextPage = data?.palApiBindRequestRequests?.pageInfo?.hasNextPage;
  const endCursor = data?.palApiBindRequestRequests?.pageInfo?.endCursor;
  const showBottomLoader =
    loading && (rows.length === 0 || networkStatus === NetworkStatus.fetchMore);

  if (!token) {
    return null;
  }

  return (
    <TableSection>
      <SectionTitle>PalApiBindRequestRequest</SectionTitle>
      <ButtonWrap>
        <Button type='button' variant='outlined' onClick={() => runQuery()}>
          {rows.length > 0 ? 'Refresh' : 'Load'}
        </Button>
      </ButtonWrap>
      <DisplayInlineError error={error} />
      <ScrollTableContainer>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <HeaderCell>ID</HeaderCell>
              <HeaderCell>Created At</HeaderCell>
              <HeaderCell>Quote ID</HeaderCell>
              <HeaderCell>Status Code</HeaderCell>
              <HeaderCell>Payment ID</HeaderCell>
              <HeaderCell>Raw Response</HeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              return (
                <TableRow key={row.id}>
                  <BodyCell>{row.id}</BodyCell>
                  <BodyCell>
                    {formatDateTimeForDisplay(row.modelCreatedAt)}
                  </BodyCell>
                  <BodyCell>
                    {row.quoteId !== null && row.quoteId !== undefined
                      ? String(row.quoteId)
                      : '—'}
                  </BodyCell>
                  <BodyCell>
                    {row.responseStatusCode !== null &&
                    row.responseStatusCode !== undefined
                      ? String(row.responseStatusCode)
                      : '—'}
                  </BodyCell>
                  <BodyCell>
                    {row.paymentInformation?.paymentId ?? '—'}
                  </BodyCell>
                  <BodyCell>
                    <JsonPreviewButton
                      value={row.responseText}
                      onOpen={setJsonDialogValue}
                    />
                  </BodyCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ScrollTableContainer>
      {hasNextPage ? (
        <LoadMoreWrap>
          <Button
            type='button'
            variant='outlined'
            disabled={loading || !endCursor}
            onClick={() => {
              if (endCursor) {
                fetchMore({
                  variables: { token, first: PAGE_SIZE, after: endCursor },
                  updateQuery: (
                    previous: PalApiBindRequestRequestsQuery,
                    { fetchMoreResult },
                  ) => {
                    if (!fetchMoreResult?.palApiBindRequestRequests) {
                      return previous;
                    }
                    return {
                      palApiBindRequestRequests: {
                        ...fetchMoreResult.palApiBindRequestRequests,
                        edges: [
                          ...(previous.palApiBindRequestRequests?.edges ?? []),
                          ...(fetchMoreResult.palApiBindRequestRequests.edges ??
                            []),
                        ],
                      },
                    };
                  },
                });
              }
            }}
          >
            Load More
          </Button>
        </LoadMoreWrap>
      ) : null}
      {showBottomLoader ? (
        <TableLoadingFooter>
          <CircularProgress size={24} />
        </TableLoadingFooter>
      ) : null}
      <JsonViewerDialog
        value={jsonDialogValue}
        onClose={() => setJsonDialogValue(null)}
      />
    </TableSection>
  );
};

const PalApiBindRequestResponsesTable = () => {
  const { token } = useAuth();
  const [runQuery, { data, loading, error, fetchMore, networkStatus }] =
    useLazyQuery<
      PalApiBindRequestResponsesQuery,
      PalApiBindRequestResponsesQueryVariables
    >(PAL_API_BIND_REQUEST_RESPONSES, {
      notifyOnNetworkStatusChange: true,
      variables: { token, first: PAGE_SIZE },
    });
  const [jsonDialogValue, setJsonDialogValue] = useState<string | null>(null);
  useEffect(() => {
    if (token) {
      runQuery();
    }
  }, [token, runQuery]);
  const rows =
    data?.palApiBindRequestResponses?.edges
      .map((edge) => edge?.node)
      .filter((node): node is PalApiBindRequestResponseNode => Boolean(node)) ??
    [];
  const hasNextPage = data?.palApiBindRequestResponses?.pageInfo?.hasNextPage;
  const endCursor = data?.palApiBindRequestResponses?.pageInfo?.endCursor;
  const showBottomLoader =
    loading && (rows.length === 0 || networkStatus === NetworkStatus.fetchMore);

  if (!token) {
    return null;
  }

  return (
    <TableSection>
      <SectionTitle>PalApiBindRequestResponse</SectionTitle>
      <ButtonWrap>
        <Button type='button' variant='outlined' onClick={() => runQuery()}>
          {rows.length > 0 ? 'Refresh' : 'Load'}
        </Button>
      </ButtonWrap>
      <DisplayInlineError error={error} />
      <ScrollTableContainer>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <HeaderCell>ID</HeaderCell>
              <HeaderCell>Created At</HeaderCell>
              <HeaderCell>Quote ID</HeaderCell>
              <HeaderCell>Bound</HeaderCell>
              <HeaderCell>Policy Number</HeaderCell>
              <HeaderCell>Errors JSON</HeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              return (
                <TableRow key={row.id}>
                  <BodyCell>{row.id}</BodyCell>
                  <BodyCell>
                    {formatDateTimeForDisplay(row.modelCreatedAt)}
                  </BodyCell>
                  <BodyCell>
                    {row.quoteId !== null && row.quoteId !== undefined
                      ? String(row.quoteId)
                      : '—'}
                  </BodyCell>
                  <BodyCell>{row.bound ? 'Yes' : 'No'}</BodyCell>
                  <BodyCell>{row.policyNumber}</BodyCell>
                  <BodyCell>
                    <JsonPreviewButton
                      value={row.errors}
                      onOpen={setJsonDialogValue}
                    />
                  </BodyCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ScrollTableContainer>
      {hasNextPage ? (
        <LoadMoreWrap>
          <Button
            type='button'
            variant='outlined'
            disabled={loading || !endCursor}
            onClick={() => {
              if (endCursor) {
                fetchMore({
                  variables: { token, first: PAGE_SIZE, after: endCursor },
                  updateQuery: (
                    previous: PalApiBindRequestResponsesQuery,
                    { fetchMoreResult },
                  ) => {
                    if (!fetchMoreResult?.palApiBindRequestResponses) {
                      return previous;
                    }
                    return {
                      palApiBindRequestResponses: {
                        ...fetchMoreResult.palApiBindRequestResponses,
                        edges: [
                          ...(previous.palApiBindRequestResponses?.edges ?? []),
                          ...(fetchMoreResult.palApiBindRequestResponses
                            .edges ?? []),
                        ],
                      },
                    };
                  },
                });
              }
            }}
          >
            Load More
          </Button>
        </LoadMoreWrap>
      ) : null}
      {showBottomLoader ? (
        <TableLoadingFooter>
          <CircularProgress size={24} />
        </TableLoadingFooter>
      ) : null}
      <JsonViewerDialog
        value={jsonDialogValue}
        onClose={() => setJsonDialogValue(null)}
      />
    </TableSection>
  );
};

interface JsonPreviewButtonProps {
  value: unknown;
  onOpen: (nextValue: string) => void;
}

const JsonPreviewButton = ({ value, onOpen }: JsonPreviewButtonProps) => {
  if (value === null || value === undefined) {
    return <span>—</span>;
  }
  const pretty = prettyJsonString(value);
  return (
    <JsonButton
      type='button'
      onClick={() => {
        onOpen(pretty);
      }}
    >
      {truncate(pretty, 100)}
    </JsonButton>
  );
};

interface JsonViewerDialogProps {
  value: string | null;
  onClose: () => void;
}

const JsonViewerDialog = ({ value, onClose }: JsonViewerDialogProps) => {
  return (
    <Dialog open={Boolean(value)} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle>JSON</DialogTitle>
      <DialogContent>
        <JsonPre>{value ?? ''}</JsonPre>
      </DialogContent>
    </Dialog>
  );
};

const truncate = (value: string, max: number): string => {
  if (value.length <= max) {
    return value;
  }
  return `${value.slice(0, max)}...`;
};

const prettyJsonString = (value: unknown): string => {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const PageSection = styled('section')(({ theme }) => {
  return { paddingTop: theme.spacing(2) };
});

const TableSection = styled('section')(({ theme }) => {
  return {
    marginTop: theme.spacing(3),
  };
});

const SectionTitle = styled('h2')(({ theme }) => {
  return {
    ...theme.typography.h6,
    marginBottom: theme.spacing(1),
  };
});

const ButtonWrap = styled('div')(({ theme }) => {
  return {
    marginBottom: theme.spacing(1),
  };
});

const ScrollTableContainer = styled(TableContainer)(({ theme }) => {
  return {
    maxWidth: '100%',
    overflowX: 'auto',
    marginTop: theme.spacing(1),
  };
});

const HeaderCell = styled(TableCell)(() => {
  return { fontWeight: 600, whiteSpace: 'nowrap' };
});

const BodyCell = styled(TableCell)(() => {
  return { whiteSpace: 'nowrap', verticalAlign: 'top' };
});

const LoadMoreWrap = styled('div')(({ theme }) => {
  return { marginTop: theme.spacing(2) };
});

const TableLoadingFooter = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  };
});

const JsonButton = styled('button')(({ theme }) => {
  return {
    border: 0,
    padding: 0,
    margin: 0,
    background: 'none',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontFamily: 'monospace',
    fontSize: theme.typography.body2.fontSize,
    textAlign: 'left',
  };
});

const JsonPre = styled('pre')(() => {
  return {
    margin: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  };
});

const PAL_QUOTE_REQUEST_REQUESTS = graphql(`
  query PalQuoteRequestRequests($token: String!, $first: Int, $after: String) {
    palQuoteRequestRequests(token: $token, first: $first, after: $after) {
      edges {
        cursor
        node {
          id
          modelCreatedAt
          responseStatusCode
          responseText
          client {
            firstName
            lastName
          }
          facility {
            name
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

const PAL_QUOTE_REQUEST_RESPONSES = graphql(`
  query PalQuoteRequestResponses($token: String!, $first: Int, $after: String) {
    palQuoteRequestResponses(token: $token, first: $first, after: $after) {
      edges {
        cursor
        node {
          id
          modelCreatedAt
          quoteId
          policyCreated
          total
          errors {
            id
            error
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

const PAL_API_BIND_REQUEST_REQUESTS = graphql(`
  query PalApiBindRequestRequests(
    $token: String!
    $first: Int
    $after: String
  ) {
    palApiBindRequestRequests(token: $token, first: $first, after: $after) {
      edges {
        cursor
        node {
          id
          modelCreatedAt
          quoteId
          responseStatusCode
          responseText
          paymentInformation {
            paymentId
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

const PAL_API_BIND_REQUEST_RESPONSES = graphql(`
  query PalApiBindRequestResponses(
    $token: String!
    $first: Int
    $after: String
  ) {
    palApiBindRequestResponses(token: $token, first: $first, after: $after) {
      edges {
        cursor
        node {
          id
          modelCreatedAt
          quoteId
          bound
          policyNumber
          errors {
            id
            error
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);
